import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { navigationManager } from '../../managers/navigation-manager';
import { MainLayout } from '../../components/Layout';
import { LoadingPanel, RevoltButton, RevoltIcon, AppLikeButton, RevoltMagicButton } from '../../components';
import { useManager } from '../../core';
import { useAuthRequired } from '../../contexts/AuthContext';
import { PlayspaceManager } from './managers/playspace-manager';
import { AppManager } from '../app/managers/app-manager';
import './PlayspaceSection.css';
import { TemplateBadge } from '../../components';
import { EntryFileErrorModal, SharingHeader, EmbedHeader } from './components';
import { RemixModal } from '../../components';
import { canShowRemixButton, canShowWorkspaceButton } from '../../core/utils/appAccessUtils';
import { envUtils } from '../../core/utils';

/**
 * PLAYSPACE MODES - For AI and Future Developers
 *
 * The playspace supports THREE distinct viewing modes, determined by URL parameters:
 *
 * 1. **NORMAL MODE** (Default)
 *    - URL: `/playspace/{appId}` (no mode parameter)
 *    - Audience: All users (anonymous can view public apps; authenticated get full controls)
 *    - UI: Full MainLayout with header, sidebar, workspace/settings/remix buttons
 *    - Features: Workspace/settings for owner/admin; remix per allowRemixing; like, refresh, fullscreen
 *    - Authentication: Optional (anonymous can view public apps)
 *
 * 2. **SHARING MODE** (`?mode=sharing`)
 *    - URL: `/playspace/{appId}?mode=sharing`
 *    - Audience: Anonymous public viewers (no login required)
 *    - UI: SharingHeader with app info, credits, auth buttons
 *    - Features: Like button, refresh, fullscreen, remix (for public apps)
 *    - Authentication: Optional (can view as guest)
 *
 * 3. **EMBED MODE** (`?mode=embed`)
 *    - URL: `/playspace/{appId}?mode=embed` or `/playspace/{appId}?mode=embed&hideHeader=true`
 *    - Audience: Anonymous embed viewers (for embedding in other sites)
 *    - UI: Optional EmbedHeader, minimal styling
 *    - Features: Like button, refresh, fullscreen, remix (for public apps)
 *    - Authentication: Optional (can view as guest)
 *
 * KEY DIFFERENCES:
 * - All modes allow anonymous access to public apps
 * - Normal mode shows full MainLayout; sharing/embed use their own headers
 * - Each mode has different headers and feature sets
 * - All modes increment view counters (with 2-hour throttling)
 * - Remix button visibility depends on membership and app public status
 */

const viewFieldMap = {
  normal: 'viewsNormal',
  sharing: 'viewsSharing',
  embedHeader: 'viewsEmbedHeader',
  embedNoHeader: 'viewsEmbedNoHeader',
  settings: 'viewsSettings',
};

export function PlayspaceSection() {
  const { appId } = useParams();
  const [searchParams] = useSearchParams();

  // All hooks must be called before any conditional logic
  const { manager, isInitialized } = useManager(PlayspaceManager);
  const { manager: appManager } = useManager(AppManager);
  const { user, loading: authLoading } = useAuthRequired({
    // For sharing mode, we allow anonymous access initially
    // Authentication will be checked in the component logic
    requireAuth: false,
    payload: {
      action: 'viewPlayspace',
      appId,
      timestamp: Date.now()
    }
  });

  const [state, setState] = useState({
    loading: true,
    app: null,
    canView: false,
    isOwner: false,
    isAdmin: false,
    error: null,
  });

  const [loadingIframe, setLoadingIframe] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ percent: 0, message: null });
  const [refreshStamp, setRefreshStamp] = useState(null);
  const [showRemixModal, setShowRemixModal] = useState(false);
  const [remixModalProject, setRemixModalProject] = useState(null);
  const [remixModalLoading, setRemixModalLoading] = useState(false);
  const [remixError, setRemixError] = useState(null);
  const [hasEntryFileError, setHasEntryFileError] = useState(false);
  const [showEntryFileErrorModal, setShowEntryFileErrorModal] = useState(false);
  const [entryFilePath, setEntryFilePath] = useState(null);
  const iframeRef = useRef(null);
  const viewportRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const uiMode = envUtils.device.getRecommendedUIMode();
    return uiMode === 'mobile';
  });
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);

  useEffect(() => {
    const check = () => {
      const uiMode = envUtils.device.getRecommendedUIMode();
      setIsMobile(uiMode === 'mobile');
    };
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const app = state.app;
  const previewUrl = app?.previewUrl;
  const ownerName = app?.ownerDisplayName || app?.ownerUsername || 'Unknown owner';
  const viewTotal = (app?.viewsNormal || 0)
    + (app?.viewsSharing || 0)
    + (app?.viewsEmbedHeader || 0)
    + (app?.viewsEmbedNoHeader || 0)
    + (app?.viewsSettings || 0);

  const handleLikeToggleResult = (result) => {
    if (!result) return;
    setState((prev) => ({
      ...prev,
      app: prev.app
        ? {
            ...prev.app,
            likes: typeof result.likes === 'number' ? result.likes : prev.app.likes,
            userLiked: typeof result.liked === 'boolean' ? result.liked : prev.app.userLiked,
          }
        : prev.app,
    }));
  };


  useEffect(() => {
    setRefreshStamp(null);
    // Only reset error states if previewUrl changes (new app loaded)
    // Don't reset if we're just updating the same app
    // The error states will be reset in the load effect when a new app is loaded
  }, [previewUrl]);

  // All hooks must be called before any conditional logic
  useEffect(() => {
    const load = async () => {
      if (!isInitialized || authLoading) return;
      setState((prev) => ({ ...prev, loading: true }));
      setLoadingProgress({ percent: 0, message: null });
      setHasEntryFileError(false);
      setShowEntryFileErrorModal(false);
      setEntryFilePath(null);
      const result = await manager.loadApp(appId, user, {
        onProgress: ({ percent, message }) => setLoadingProgress({ percent, message }),
      });

      const app = result?.app;
      const canView = !!result?.canView;
      const isOwner = !!result?.isOwner;
      const isAdmin = !!result?.isAdmin;
      const error = result?.error || null;

      // Increment view counter for any valid viewer.
      // Backend applies per-viewer, per-mode 2-hour throttling.
      if (app && canView && !error) {
        const isSharingMode = searchParams.get('mode') === 'sharing';
        const isEmbedMode = searchParams.get('mode') === 'embed';
        const hideHeader = searchParams.get('hideHeader') === 'true';

        let mode = 'normal';
        if (isSharingMode) {
          mode = 'sharing';
        } else if (isEmbedMode) {
          mode = hideHeader ? 'embedNoHeader' : 'embedHeader';
        }

        // Fire and forget - don't block UI on view increment
        fetch(`/api/apps/${appId}/view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ mode })
        })
          .then(async (res) => {
            const data = await res.json();
            if (!data?.success || data?.throttled) return;
            const fieldName = viewFieldMap[mode];
            if (!fieldName) return;
            setState((prev) => {
              if (!prev.app) return prev;
              return {
                ...prev,
                app: {
                  ...prev.app,
                  [fieldName]: (prev.app[fieldName] || 0) + 1,
                },
              };
            });
          })
          .catch(error => {
            console.warn('Failed to increment view counter:', error);
          });
      }

      let hasError = false;
      if (app && canView && !error) {
        // Use entryFileExists from the backend response
        if (app.settings?.entryFilePath) {
          setEntryFilePath(app.settings.entryFilePath);

          // Check if entry file exists (explicitly false means it doesn't exist)
          // entryFileExists can be: true (exists), false (doesn't exist), or undefined (not checked)
          if (app.entryFileExists === false) {
            hasError = true;
            setHasEntryFileError(true);
            setShowEntryFileErrorModal(true);
          }
        }
      }

      // When load succeeded, show 100% and "Ready" briefly so the user sees the bar complete
      if (app && canView && !error) {
        setLoadingProgress({ percent: 100, message: "Ready" });
        await new Promise((r) => setTimeout(r, 350));
      }

      setState({
        loading: false,
        app: app || null,
        canView: canView,
        isOwner,
        isAdmin,
        error: error,
      });

      // If entry file doesn't exist, don't wait for iframe to load
      if (hasError) {
        setLoadingIframe(false);
      } else {
        setLoadingIframe(true);
      }
    };
    load();
  }, [appId, isInitialized, authLoading, user, manager, searchParams]);

  const handleFullscreen = async () => {
    // On mobile (especially iOS Safari), Fullscreen API doesn't work for non-video elements.
    // Use CSS-based pseudo-fullscreen as fallback.
    if (isMobile) {
      setIsPseudoFullscreen((prev) => !prev);
      return;
    }
    const el = viewportRef.current || iframeRef.current;
    if (!el) return;
    try {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.webkitRequestFullScreen;
      if (req) {
        await req.call(el);
      } else {
        setIsPseudoFullscreen((prev) => !prev);
      }
    } catch {
      setIsPseudoFullscreen((prev) => !prev);
    }
  };

  const handleExitPseudoFullscreen = () => {
    setIsPseudoFullscreen(false);
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setIsPseudoFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  // Hide site header when in pseudo-fullscreen (fixes mobile where header overlays fullscreen)
  useEffect(() => {
    if (isPseudoFullscreen) {
      document.body.classList.add('playspace-pseudo-fullscreen');
    } else {
      document.body.classList.remove('playspace-pseudo-fullscreen');
    }
    return () => document.body.classList.remove('playspace-pseudo-fullscreen');
  }, [isPseudoFullscreen]);

  const handleOpenSettings = async () => {
    if (!appId) return;
    await manager.openAppSettings(appId);
  };

  const handleOpenWorkspace = async () => {
    if (!appId) return;

    await navigationManager.navigateToPath(`/workspace/${appId}`);
  };

  const handleRemix = () => {
    setRemixModalProject(app);
    setShowRemixModal(true);
    setRemixError(null);
  };

  const handleConfirmRemix = async (project) => {
    // Anonymous users must sign in to remix
    if (!user) {
      setShowRemixModal(false);
      setRemixModalProject(null);
      await navigationManager.requireAuth(window.location.pathname + window.location.search);
      return;
    }

    setRemixModalLoading(true);
    setRemixError(null);

    try {
      const response = await fetch('/api/apps/remix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sourceAppId: project._id || project.id,
          name: `${project.name} (Remix)`,
          description: project.description || '',
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowRemixModal(false);
        setRemixModalProject(null);
        setRemixModalLoading(false);
        setRemixError(null);

        // Navigate to the workspace (same as workspace button)
        await navigationManager.navigateToPath(`/workspace/${result.app._id}`);
      } else {
        setRemixError(result.error || 'Failed to remix app');
        setRemixModalLoading(false);
      }
    } catch (error) {
      setRemixError(error.message || 'Failed to remix app');
      setRemixModalLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoadingIframe(true);
    setHasEntryFileError(false);
    setShowEntryFileErrorModal(false);

    if (hasEntryFileError) {
      setLoadingIframe(false);
      return;
    }

    setRefreshStamp(Date.now());
  };

  const handleIframeLoad = () => {
    setLoadingIframe(false);
  };

  const handleIframeError = () => {
    setLoadingIframe(false);
    // The proactive check in useEffect will handle showing the error modal
  };

  const iframeSrc = useMemo(() => {
    if (!previewUrl || hasEntryFileError) {
      return 'about:blank';
    }
    if (!refreshStamp) {
      return previewUrl;
    }
    const separator = previewUrl.includes('?') ? '&' : '?';
    return `${previewUrl}${separator}_r=${refreshStamp}`;
  }, [previewUrl, refreshStamp, hasEntryFileError]);

  // Handle case where iframe src is about:blank (won't trigger onLoad)
  useEffect(() => {
    if (iframeSrc === 'about:blank' && hasEntryFileError) {
      // If we have an error and iframe is blank, stop loading immediately
      setLoadingIframe(false);
    }
  }, [iframeSrc, hasEntryFileError]);

  // Mode detection: Determine if we're in sharing mode (?mode=sharing) or embed mode (?mode=embed)
  // Normal mode has no mode parameter and is the default authenticated experience
  const isSharingMode = searchParams.get('mode') === 'sharing';
  const isEmbedMode = searchParams.get('mode') === 'embed';
  const hideHeader = searchParams.get('hideHeader') === 'true';
  const remixModalElement = (
    <RemixModal
      isOpen={showRemixModal}
      onClose={() => { setShowRemixModal(false); setRemixError(null); }}
      onConfirmRemix={handleConfirmRemix}
      project={remixModalProject}
      isLoading={remixModalLoading}
      errorMessage={remixError}
    />
  );

  // SHARING MODE RENDERING: Anonymous access with SharingHeader (app info, credits, auth buttons)
  if (isSharingMode) {
    // For sharing mode, we need to load the app anonymously if possible
    // Follow the same loading pattern as regular playspace

    // Show loading panel during app data loading (pure app loading experience)
    if (state.loading) {
      return (
        <div className="sharing-loading-container">
          <LoadingPanel
            title="Loading Playspace"
            appId={appId}
            subtitle="Preparing your app for viewing..."
            progress={loadingProgress.percent}
            statusMessage={loadingProgress.message}
          />
        </div>
      );
    }

    // Show error for API failures or access issues
    if (state.error) {
      const isPrivateAppError = state.error === 'Authentication required for private apps';
      const errorTitle = state.error === 'not_found' ? 'App Not Found' :
                        state.error === 'forbidden' || isPrivateAppError ? 'Access Denied' : 'Error Loading App';
      const errorSubtitle = state.error === 'not_found' ? 'The requested app could not be found.' :
                           state.error === 'forbidden' || isPrivateAppError ? 'This app is not publicly available.' :
                           'An error occurred while loading the app.';

      return (
        <div className="sharing-loading-container">
          <LoadingPanel
            title={errorTitle}
            appId={appId}
            subtitle={errorSubtitle}
          />
        </div>
      );
    }

    // Show error for non-public apps (pure error experience)
    if (!state.canView) {
      return (
        <div className="sharing-loading-container">
          <LoadingPanel
            title="Access Denied"
            appId={appId}
            subtitle="This app is not publicly available."
          />
        </div>
      );
    }

    // App data loaded - show sharing interface with header
    return (
      <div className={`sharing-mode-container ${isPseudoFullscreen ? 'pseudo-fullscreen-active' : ''}`}>
        {!isPseudoFullscreen && (
        <SharingHeader
          app={app}
          appId={appId}
          viewTotal={viewTotal}
          appManager={appManager}
          onLikeToggle={handleLikeToggleResult}
          onRefresh={handleRefresh}
          onFullscreen={handleFullscreen}
          onRemix={handleRemix}
          onOpenWorkspace={handleOpenWorkspace}
          onOpenSettings={handleOpenSettings}
          isOwner={state.isOwner}
          isAdmin={state.isAdmin}
          refreshDisabled={loadingIframe}
        />
        )}
        <div ref={viewportRef} className="sharing-viewport">
          {loadingIframe && (
            <div className="sharing-iframe-overlay">
              <div className="sharing-iframe-spinner" />
              <div className="sharing-overlay-text">Loading app…</div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            title="Shared App"
            src={iframeSrc}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            className="sharing-iframe"
          />
          {isPseudoFullscreen && (
            <button
              type="button"
              className="playspace-exit-fullscreen-btn"
              onClick={handleExitPseudoFullscreen}
              title="Exit full screen"
              aria-label="Exit full screen"
            >
              <i className="fa-solid fa-compress"></i>
            </button>
          )}
        </div>
        {remixModalElement}
      </div>
    );
  }

  // EMBED MODE RENDERING: Anonymous access with optional EmbedHeader (minimal, embeddable UI)
  if (isEmbedMode) {
    // Show loading panel during app data loading
    if (state.loading) {
      return (
        <div className="embed-loading-container">
          <LoadingPanel
            title="Loading Playspace"
            appId={appId}
            subtitle="Preparing your app for viewing..."
            progress={loadingProgress.percent}
            statusMessage={loadingProgress.message}
          />
        </div>
      );
    }

    // Show error for API failures or access issues
    if (state.error) {
      const isPrivateAppError = state.error === 'Authentication required for private apps';
      const errorTitle = state.error === 'not_found' ? 'App Not Found' :
                        state.error === 'forbidden' || isPrivateAppError ? 'Access Denied' : 'Error Loading App';
      const errorSubtitle = state.error === 'not_found' ? 'The requested app could not be found.' :
                           state.error === 'forbidden' || isPrivateAppError ? 'This app is not publicly available.' :
                           'An error occurred while loading the app.';
      
      return (
        <div className="embed-loading-container">
          <LoadingPanel
            title={errorTitle}
            appId={appId}
            subtitle={errorSubtitle}
          />
        </div>
      );
    }

    if (!state.canView) {
      return (
        <div className="embed-loading-container">
          <LoadingPanel
            title="Access Denied"
            appId={appId}
            subtitle="This app is not publicly available."
          />
        </div>
      );
    }

    // App data loaded - show embed interface with optional header
    return (
      <div className={`embed-mode-container ${isPseudoFullscreen ? 'pseudo-fullscreen-active' : ''}`}>
        {!isPseudoFullscreen && !hideHeader && (
          <EmbedHeader
            app={app}
            appId={appId}
            viewTotal={viewTotal}
            appManager={appManager}
            onLikeToggle={handleLikeToggleResult}
            onRefresh={handleRefresh}
            onFullscreen={handleFullscreen}
            onRemix={handleRemix}
            onOpenWorkspace={handleOpenWorkspace}
            onOpenSettings={handleOpenSettings}
            isOwner={state.isOwner}
            isAdmin={state.isAdmin}
            refreshDisabled={loadingIframe}
          />
        )}
        <div ref={viewportRef} className={`embed-viewport ${hideHeader ? 'full-height' : ''}`}>
          {loadingIframe && (
            <div className="embed-iframe-overlay">
              <div className="embed-iframe-spinner" />
              <div className="embed-overlay-text">Loading app…</div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            title="Embedded App"
            src={iframeSrc}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            className="embed-iframe"
          />
          {isPseudoFullscreen && (
            <button
              type="button"
              className="playspace-exit-fullscreen-btn"
              onClick={handleExitPseudoFullscreen}
              title="Exit full screen"
              aria-label="Exit full screen"
            >
              <i className="fa-solid fa-compress"></i>
            </button>
          )}
        </div>
        {remixModalElement}
      </div>
    );
  }

  if (!isInitialized || authLoading || state.loading) {
    return (
      <MainLayout contentClassName="playspace-section">
        <div className="loading-container">
          <LoadingPanel
            title="Loading Playspace"
            appId={appId}
            subtitle="Preparing your app for viewing..."
            progress={loadingProgress.percent}
            statusMessage={loadingProgress.message}
          />
        </div>
      </MainLayout>
    );
  }

  if (state.error === 'not_found') {
    return (
      <MainLayout contentClassName="playspace-section">
        <div className="loading-container">
          <LoadingPanel
            title="App Not Found"
            appId={appId}
            subtitle="The requested application could not be found."
          />
        </div>
      </MainLayout>
    );
  }

  if (state.error === 'unauthorized') {
    return (
      <MainLayout contentClassName="playspace-section">
        <div className="loading-container">
          <LoadingPanel
            title="Access Denied"
            appId={appId}
            subtitle="Please sign in to view this app's playspace."
          />
        </div>
      </MainLayout>
    );
  }


  if (!state.canView) {
    return (
      <MainLayout contentClassName="playspace-section">
        <div className="loading-container">
          <LoadingPanel
            title="Access Denied"
            appId={appId}
            subtitle="You do not have permission to view this app's playspace."
          />
        </div>
      </MainLayout>
    );
  }

  const canOpenWorkspace = canShowWorkspaceButton({ isOwner: state.isOwner, isAdmin: state.isAdmin });
  const isNormalMode = !isSharingMode && !isEmbedMode;

  // Remix logic using centralized helper
  const canRemix = isNormalMode && canShowRemixButton(app, { isOwner: state.isOwner, isAdmin: state.isAdmin });

  return (
    <MainLayout contentClassName="playspace-section">
      <div className={`playspace-container ${isPseudoFullscreen ? 'pseudo-fullscreen-active' : ''}`}>
        {!isPseudoFullscreen && (
        <div className="playspace-header">
          <div className="playspace-header-row playspace-header-row-1">
            <div className="playspace-title">{app?.name || 'Untitled App'}</div>
          </div>
          <div className="playspace-header-row playspace-header-row-2">
            <div className="playspace-subtitle">by {ownerName}</div>
            <div className="playspace-meta">
              <TemplateBadge template={app?.template} appType={app?.appType} />
            </div>
          </div>
          <div className="playspace-header-row playspace-header-row-3">
            {isNormalMode && (
              <div className="playspace-stats">
                <div className="playspace-stat">
                  <RevoltIcon name="eye" size="sm" />
                  <span>{viewTotal}</span>
                </div>
                <AppLikeButton
                  appId={appId}
                  initialLikes={app?.likes || 0}
                  initialLiked={app?.userLiked || false}
                  manager={appManager}
                  onToggle={handleLikeToggleResult}
                />
              </div>
            )}
            <div className="playspace-actions">
              {canRemix && (
                <RevoltMagicButton onClick={handleRemix} title="Remix">
                  <RevoltIcon name="lucide:wand-sparkles" size="sm" />
                  {!isMobile && <span className="playspace-btn-text">Remix</span>}
                </RevoltMagicButton>
              )}
              {canOpenWorkspace && (
                <RevoltButton
                  className="playspace-button"
                  as="a"
                  href={`/app-settings/${appId}`}
                  data-navigation-button
                  onClick={async (e) => {
                    e.preventDefault();
                    await handleOpenSettings();
                  }}
                  title="Settings"
                >
                  <i className="fa-solid fa-gear"></i>
                  {!isMobile && <span className="playspace-btn-text"> Settings</span>}
                </RevoltButton>
              )}
              {canOpenWorkspace && (
                <RevoltButton
                  className="playspace-button"
                  as="a"
                  href={`/workspace/${appId}`}
                  data-navigation-button
                  onClick={async (e) => {
                    e.preventDefault();
                    await handleOpenWorkspace();
                  }}
                  title="Workspace"
                >
                  <i className="fa-solid fa-code"></i>
                  {!isMobile && <span className="playspace-btn-text"> Workspace</span>}
                </RevoltButton>
              )}
              <RevoltButton
                className="playspace-button"
                onClick={handleRefresh}
                disabled={loadingIframe}
                title="Refresh"
              >
                <i className="fa-solid fa-rotate-right"></i>
                {!isMobile && <span className="playspace-btn-text"> Refresh</span>}
              </RevoltButton>
              <RevoltButton
                className="playspace-button"
                onClick={handleFullscreen}
                title="Full Screen"
              >
                <i className="fa-solid fa-expand"></i>
                {!isMobile && <span className="playspace-btn-text"> Full Screen</span>}
              </RevoltButton>
            </div>
          </div>
        </div>
        )}

        <div ref={viewportRef} className="playspace-viewport">
          {loadingIframe && (
            <div className="playspace-iframe-overlay">
              <div className="playspace-spinner" />
              <div className="playspace-overlay-text">Loading app…</div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            title="App Playspace"
            src={iframeSrc}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            className="playspace-iframe"
          />
          {isPseudoFullscreen && (
            <button
              type="button"
              className="playspace-exit-fullscreen-btn"
              onClick={handleExitPseudoFullscreen}
              title="Exit full screen"
              aria-label="Exit full screen"
            >
              <i className="fa-solid fa-compress"></i>
            </button>
          )}
        </div>
      </div>
      <EntryFileErrorModal
        isOpen={showEntryFileErrorModal}
        onClose={() => setShowEntryFileErrorModal(false)}
        filePath={entryFilePath || state.app?.settings?.entryFilePath}
      />
      <RemixModal
        isOpen={showRemixModal}
        onClose={() => { setShowRemixModal(false); setRemixError(null); }}
        onConfirmRemix={handleConfirmRemix}
        project={remixModalProject}
        isLoading={remixModalLoading}
        errorMessage={remixError}
      />
    </MainLayout>
  );
}


